import api, { resolveApiBaseUrl } from "@/lib/axios";

import { parseSseChunks } from "./sse";
import type { AssistantConversation, QuotaError, StreamEvent } from "./types";

interface Envelope<T> {
  errors: boolean;
  data: T;
}

export class QuotaExceededError extends Error {
  constructor(public quota: QuotaError) {
    super(quota.message);
    this.name = "QuotaExceededError";
  }
}

export const assistantApi = {
  async listConversations() {
    const { data } = await api.get<Envelope<AssistantConversation[]>>(
      "assistant/conversations",
    );
    return data.data;
  },

  async createConversation() {
    const { data } = await api.post<Envelope<AssistantConversation>>(
      "assistant/conversations",
    );
    return data.data;
  },

  async getConversation(id: number) {
    const { data } = await api.get<Envelope<AssistantConversation>>(
      `assistant/conversations/${id}`,
    );
    return data.data;
  },

  async deleteConversation(id: number) {
    await api.delete(`assistant/conversations/${id}`);
  },

  async sendFeedback(messageId: number, feedback: "up" | "down") {
    await api.post(`assistant/messages/${messageId}/feedback`, { feedback });
  },

  /** Envia a mensagem e consome o SSE. axios não streama; usamos fetch + ReadableStream. */
  async streamMessage(
    conversationId: number,
    content: string,
    onEvent: (event: StreamEvent) => void,
    signal?: AbortSignal,
  ) {
    const token = localStorage.getItem("verden-token");
    const response = await fetch(
      `${resolveApiBaseUrl(window.location.hostname)}/assistant/conversations/${conversationId}/messages`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ content }),
        signal,
      },
    );

    if (response.status === 429) {
      // Cota diária (tem resets_at) vs. throttle por minuto (não tem).
      const body = (await response
        .json()
        .catch(() => null)) as Envelope<QuotaError> | null;
      if (body?.data?.resets_at) throw new QuotaExceededError(body.data);
      throw new Error(
        "Muitas mensagens em pouco tempo. Aguarde um minuto e tente de novo.",
      );
    }
    if (response.status === 401) {
      // Mesmo comportamento do interceptor de 401 do axios: sem refresh token, só relogar.
      localStorage.removeItem("verden-token");
      window.location.href = "/auth/login";
      throw new Error("Sessão expirada.");
    }
    if (!response.ok || !response.body) {
      throw new Error(`Falha ao enviar a mensagem (${response.status}).`);
    }

    const parse = parseSseChunks((event, data) => {
      onEvent({ event, data: JSON.parse(data) } as StreamEvent);
    });
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      parse(decoder.decode(value, { stream: true }));
    }
    parse("", true);
  },
};
